import PageBack from '../components/PageBack';
import { getBlogIndex, type BlogPost } from '../../../shared-web/site-content-runtime';

/**
 * /blog, /blog/page/N, and /blog/category/<slug> — all the same component.
 *
 * They differ only in which slice of posts the edge resolved and whether a
 * category is set, so one component keeps the card markup, the pagination and
 * the category nav from drifting into three near-identical copies.
 */
export default function BlogIndex() {
  const data = getBlogIndex();
  const category = data?.category ?? null;
  const posts = data?.posts ?? [];
  const categories = data?.categories ?? [];
  const page = data?.page ?? 1;
  const pageCount = data?.pageCount ?? 1;
  const path = category ? `/blog/category/${category.slug}` : '/blog';

  const heading = category ? category.name : 'The Ember blog';
  const blurb = category?.description
    || 'Writing about the situations people bring to Ember — and how it went for the people who stood in them.';

  return (
    <div className="flex min-h-screen flex-col bg-[#f9f5e9] text-ink" style={BODY}>
      <main className="eh-page-top mx-auto w-full max-w-4xl flex-1 px-6 pb-16 sm:px-10">
        <div className="eh-page-back-row">
          <PageBack />
        </div>
        {category && (
          <nav className="mb-6 text-sm text-ink/60" aria-label="Breadcrumb">
            <a className="hover:underline" href="/">Ember</a>
            <span className="mx-2" aria-hidden="true">/</span>
            <a className="hover:underline" href="/blog">Blog</a>
          </nav>
        )}

        <header className="mb-10 max-w-2xl">
          <h1 className="mb-3 text-4xl font-bold tracking-tight" style={HEADING}>
            {heading}
            {/* The page number belongs in the H1 of a paginated view: without
                it, page 3 has the same title and heading as page 1, which reads
                as the same page to a person and as a duplicate to a crawler. */}
            {page > 1 ? <span className="text-ink/50"> — page {page}</span> : null}
          </h1>
          <p className="text-lg leading-relaxed text-ink/80" style={BODY}>{blurb}</p>
        </header>

        {categories.length > 0 && (
          <nav className="mb-10 flex flex-wrap gap-2" aria-label="Categories">
            <CategoryChip href="/blog" label="All" active={!category} />
            {categories.map((c) => (
              <CategoryChip
                key={c.slug}
                href={`/blog/category/${c.slug}`}
                label={c.name}
                active={category?.slug === c.slug}
              />
            ))}
          </nav>
        )}

        {posts.length === 0 ? (
          <div className="rounded-xl bg-white/60 p-10 text-center" style={BODY}>
            <p className="text-ink/80">Nothing published here yet.</p>
          </div>
        ) : (
          <ul className="grid gap-6 sm:grid-cols-2">
            {posts.map((post, index) => <PostCard key={post.slug} post={post} position={index + 1} />)}
          </ul>
        )}

        {pageCount > 1 && <Pagination page={page} pageCount={pageCount} basePath={path} />}
      </main>
    </div>
  );
}

function CategoryChip({ href, label, active }: { href: string; label: string; active: boolean }) {
  return (
    <a
      href={href}
      data-log={`blog_category_${active ? 'active' : 'link'}`}
      className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
        active
          ? 'border-ink/30 bg-white text-ink'
          : 'border-ink/15 text-ink/70 hover:border-ink/30 hover:text-ink'
      }`}
      style={BODY}
    >
      {label}
    </a>
  );
}

function PostCard({ post, position }: { post: BlogPost; position: number }) {
  return (
    <li>
      <a
        className="flex h-full flex-col overflow-hidden rounded-xl bg-white/60 transition-colors hover:bg-white"
        href={`/blog/${post.slug}`}
        data-log={`blog_card_${post.slug}`}
        data-log-seen={`blog_card_${post.slug}`}
        data-log-position={position}
      >
        {post.image_url && (
          <img
            className="aspect-[16/9] w-full object-cover"
            src={post.image_url}
            alt={`${post.title} — article illustration`}
            loading="lazy"
          />
        )}
        <div className="flex flex-1 flex-col p-6">
          {post.category_name && (
            <p className="mb-2 text-xs uppercase tracking-wide text-ink/50" style={BODY}>
              {post.category_name}
            </p>
          )}
          <h2 className="mb-2 text-xl font-semibold" style={HEADING}>{post.title}</h2>
          {post.excerpt && (
            <p className="mb-4 flex-1 text-sm leading-relaxed text-ink/70" style={BODY}>
              {post.excerpt}
            </p>
          )}
          <PostMeta post={post} />
        </div>
      </a>
    </li>
  );
}

export function PostMeta({ post }: { post: BlogPost }) {
  const date = formatDate(post.published_at);
  if (!post.author_name && !date) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-ink/60" style={BODY}>
      {post.author_image_url && (
        <img className="size-6 rounded-full object-cover" src={post.author_image_url} alt="" loading="lazy" />
      )}
      {post.author_name && <span>{post.author_name}</span>}
      {post.author_name && date && <span aria-hidden="true">·</span>}
      {/* A machine-readable date next to the human one: this is the value the
          BlogPosting schema claims, and a crawler should be able to see them
          agree on the page itself. */}
      {date && <time dateTime={post.published_at}>{date}</time>}
    </div>
  );
}

export function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Numbered pagination rather than an infinite scroll or a bare "next".
 *
 * Every page has to be reachable by a crawler following plain links, and the
 * links have to be in the HTML — that is how the articles past page one get
 * discovered at all. Page 1 is `/blog`, never `/blog/page/1`: two URLs for one
 * page of results is a duplicate nobody gains anything from.
 */
function Pagination({ page, pageCount, basePath }: { page: number; pageCount: number; basePath: string }) {
  const href = (n: number) => (n <= 1 ? basePath : `${basePath}/page/${n}`);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  return (
    <nav className="mt-12 flex flex-wrap items-center justify-center gap-2" aria-label="Pagination">
      {page > 1 && (
        <a className="px-3 py-1.5 text-sm text-ink/70 underline" href={href(page - 1)} data-log="blog_page_prev">
          Previous
        </a>
      )}
      {pages.map((n) => (
        <a
          key={n}
          href={href(n)}
          data-log="blog_page_number"
          aria-current={n === page ? 'page' : undefined}
          className={`rounded-md px-3 py-1.5 text-sm ${
            n === page ? 'bg-white font-semibold text-ink' : 'text-ink/70 hover:bg-white/60'
          }`}
          style={BODY}
        >
          {n}
        </a>
      ))}
      {page < pageCount && (
        <a className="px-3 py-1.5 text-sm text-ink/70 underline" href={href(page + 1)} data-log="blog_page_next">
          Next
        </a>
      )}
    </nav>
  );
}

/* The SPA's --eh-sans stack (EmberApp.css) verbatim, so blog type reads as
   the same family as /requests. The marketing bundle has no --eh-* tokens. */
const EH_SANS = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans CJK SC", "Nirmala UI", "Geeza Pro", sans-serif';

// one title face across the site (2026-09-06 走查): headings speak, so the display serif
const EH_SERIF = '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Palatino, "Hoefler Text", Georgia, serif';
const HEADING = {
  fontFamily: EH_SERIF,
  fontWeight: 400,
  letterSpacing: '-0.015em',
  color: '#3A331D',
} as const;

const BODY = {
  fontFamily: EH_SANS,
} as const;
